import { z } from 'zod';

/**
 * Enum values shared by the database schema, the API contracts, and the UI.
 * The database enum types are generated from these arrays, so adding a value
 * here and running `db:generate` keeps the two in step.
 */

/**
 * `coach` sits between the two: it grants the ability to create and run student
 * groups, and nothing else. A coach cannot edit curriculum, cannot manage
 * accounts, and cannot see anyone's email — the powers an admin has. Making it
 * a role rather than a per-group flag means "who may create a group at all" is
 * one check, and an admin grants it by changing one field.
 *
 * Appended rather than inserted before `admin`, so the PostgreSQL enum gains a
 * value at the end and existing rows never need rewriting. Nothing orders by
 * role, so the position carries no meaning.
 */
export const USER_ROLES = ['learner', 'admin', 'coach'] as const;
export const userRoleSchema = z.enum(USER_ROLES);
export type UserRole = z.infer<typeof userRoleSchema>;

/** Roles allowed to create and administer groups. */
export const GROUP_MANAGER_ROLES: readonly UserRole[] = ['admin', 'coach'];

/** Ordered easiest to hardest; the order is meaningful and relied upon. */
export const DIFFICULTIES = ['beginner', 'easy', 'medium', 'hard', 'expert'] as const;
export const difficultySchema = z.enum(DIFFICULTIES);
export type Difficulty = z.infer<typeof difficultySchema>;

export const DIFFICULTY_RANK: Record<Difficulty, number> = {
  beginner: 0,
  easy: 1,
  medium: 2,
  hard: 3,
  expert: 4,
};

/** Suggested XP for an exercise at each difficulty, used when authoring content. */
export const DIFFICULTY_XP: Record<Difficulty, number> = {
  beginner: 10,
  easy: 20,
  medium: 35,
  hard: 55,
  expert: 80,
};

export const EXERCISE_KINDS = ['lesson', 'practice', 'interview'] as const;
export const exerciseKindSchema = z.enum(EXERCISE_KINDS);
export type ExerciseKind = z.infer<typeof exerciseKindSchema>;

export const LESSON_STATUSES = ['not_started', 'in_progress', 'completed'] as const;
export const lessonStatusSchema = z.enum(LESSON_STATUSES);
export type LessonStatus = z.infer<typeof lessonStatusSchema>;

export const XP_REASONS = ['exercise', 'lesson', 'badge', 'streak'] as const;
export const xpReasonSchema = z.enum(XP_REASONS);
export type XpReason = z.infer<typeof xpReasonSchema>;

export const QUERY_STATUSES = ['ok', 'error', 'timeout', 'rejected'] as const;
export const queryStatusSchema = z.enum(QUERY_STATUSES);
export type QueryStatus = z.infer<typeof queryStatusSchema>;

export const INTERVIEW_CATEGORIES = [
  'basics',
  'aggregation',
  'joins',
  'subqueries',
  'ctes',
  'window_functions',
  'date_analysis',
  'business_analytics',
] as const;
export const interviewCategorySchema = z.enum(INTERVIEW_CATEGORIES);
export type InterviewCategory = z.infer<typeof interviewCategorySchema>;

export const INTERVIEW_CATEGORY_LABELS: Record<InterviewCategory, string> = {
  basics: 'SQL Basics',
  aggregation: 'Aggregation',
  joins: 'Joins',
  subqueries: 'Subqueries',
  ctes: 'CTEs',
  window_functions: 'Window Functions',
  date_analysis: 'Date Analysis',
  business_analytics: 'Business Analytics',
};
